/**
 * iter-312t: Inline payment recording from Edit BOQ.
 *
 * Lets admin/owner record a Client Payment Received OR a Vendor Payment Made
 * without leaving the BOQ. Writes via the same canonical endpoints used by
 * Project Accounts, so the new payment is immediately visible in the project
 * ledger AND in the Apply Payments modal of this BOQ — single source of truth.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CreditCard, Banknote, Wallet } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function RecordPaymentDialog({ open, onOpenChange, projectId, mode = 'client', onRecorded, worksheetId, legendCategories }) {
  const isVendor = mode === 'vendor';
  const [amount, setAmount] = useState('');
  const [paymentMode, setPaymentMode] = useState('online');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [isAdvance, setIsAdvance] = useState(false);
  const [saving, setSaving] = useState(false);
  // Vendor mode only — pick the vendor.
  const [vendors, setVendors] = useState([]);
  const [vendorId, setVendorId] = useState('');
  // iter-338i: Legend Category attribution for vendor payment → BOQ profitability
  const [legendCode, setLegendCode] = useState('');

  const reload = useCallback(async () => {
    if (!isVendor) return;
    try {
      const r = await axios.get(`${API}/users?role=vendor`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setVendors(r.data || []);
    } catch { /* non-fatal */ }
  }, [isVendor]);

  useEffect(() => {
    if (open) {
      reload();
      setAmount(''); setReference(''); setNotes('');
      setPaymentDate(new Date().toISOString().slice(0, 10));
      setPaymentMode('online'); setIsAdvance(false);
      setLegendCode('');
    }
  }, [open, reload]);

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error('Enter a positive amount'); return; }
    if (isVendor && !vendorId) { toast.error('Pick a vendor'); return; }
    if (!projectId) { toast.error('Missing project link'); return; }
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const baseBody = {
        amount: amt,
        payment_mode: paymentMode,
        payment_date: new Date(paymentDate).toISOString(),
        notes: notes
          ? (reference ? `${reference} — ${notes}` : notes)
          : (reference || (isAdvance ? 'Advance payment' : '')),
      };
      if (isVendor) {
        // iter-338i: attach legend category + BOQ id for profitability tracking
        const legendCat = (legendCategories || []).find(
          (c) => (c.code || c.group) === legendCode
        );
        await axios.post(`${API}/projects/${projectId}/record-vendor-payment`, {
          ...baseBody,
          vendor_id: vendorId,
          legend_category_code: legendCode || undefined,
          legend_category_name: legendCat ? (legendCat.name || legendCat.group_name) : undefined,
          boq_worksheet_id: worksheetId || undefined,
        }, { headers });
        toast.success('Vendor payment recorded — visible in Project Accounts');
      } else {
        await axios.post(`${API}/projects/${projectId}/record-client-payment`, baseBody, { headers });
        toast.success('Client payment recorded — visible in Project Accounts');
      }
      onRecorded && onRecorded();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg !max-h-[88vh] flex flex-col overflow-hidden p-0"
        style={{ zoom: 1.25 }}
        data-testid="record-payment-dialog"
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-stone-200 bg-white flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <CreditCard size={18} className={isVendor ? 'text-blue-600' : 'text-emerald-600'} />
            Record {isVendor ? 'Vendor Payment Made' : 'Client Payment Received'}
          </DialogTitle>
          <DialogDescription className="text-xs text-stone-500">
            This payment is written directly to Project Accounts and will be available in this BOQ's <em>Apply Payments</em> picker immediately.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3 min-h-0">
          {isVendor && (
            <div>
              <Label className="text-xs">Vendor *</Label>
              <select
                value={vendorId}
                onChange={(e) => setVendorId(e.target.value)}
                className="w-full h-9 text-sm border border-stone-200 rounded px-2 outline-none focus:ring-1 focus:ring-blue-300"
                data-testid="rp-vendor"
              >
                <option value="">Select a vendor…</option>
                {vendors.map(v => (
                  <option key={v.id} value={v.id}>{v.name || v.email}{v.vendor_company_name ? ` (${v.vendor_company_name})` : ''}</option>
                ))}
              </select>
            </div>
          )}

          {/* iter-338i: Legend Category attribution (vendor mode only).
             Links this payment to a BOQ Main Category so the Profitability
             tab can attribute Actual Vendor Cost per category. */}
          {isVendor && legendCategories && legendCategories.length > 0 && (
            <div>
              <Label className="text-xs">Legend Category (for Profitability)</Label>
              <select
                value={legendCode}
                onChange={(e) => setLegendCode(e.target.value)}
                className="w-full h-9 text-sm border border-stone-200 rounded px-2 outline-none focus:ring-1 focus:ring-blue-300"
                data-testid="rp-legend-category"
              >
                <option value="">— Uncategorized (assign later) —</option>
                {legendCategories.map((c) => {
                  const code = c.code || c.group;
                  const name = c.name || c.group_name;
                  return <option key={code} value={code}>{code} — {name}</option>;
                })}
              </select>
              <p className="text-[10px] text-stone-500 mt-0.5">Links this payment to a BOQ category for profitability tracking.</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Amount (₹) *</Label>
              <Input type="number" min="0" step="1" value={amount} onChange={e => setAmount(e.target.value)} placeholder="e.g. 50000" data-testid="rp-amount" />
            </div>
            <div>
              <Label className="text-xs">Date *</Label>
              <Input type="date" value={paymentDate} onChange={e => setPaymentDate(e.target.value)} data-testid="rp-date" />
            </div>
          </div>

          <div>
            <Label className="text-xs">Payment Mode *</Label>
            <div className="flex flex-wrap gap-2">
              {[
                { v: 'online', label: 'Online', icon: <Banknote size={12} /> },
                { v: 'bank_transfer', label: 'Bank Transfer', icon: <Banknote size={12} /> },
                { v: 'cash', label: 'Cash', icon: <Wallet size={12} /> },
                { v: 'cheque', label: 'Cheque', icon: <CreditCard size={12} /> },
              ].map(m => (
                <button
                  key={m.v}
                  type="button"
                  onClick={() => setPaymentMode(m.v)}
                  className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition-colors ${paymentMode === m.v ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-stone-700 border-stone-300 hover:border-stone-400'}`}
                  data-testid={`rp-mode-${m.v}`}
                >
                  {m.icon}{m.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs">Reference # (optional)</Label>
            <Input value={reference} onChange={e => setReference(e.target.value)} placeholder="e.g. ICICI-TXN-9F4C" data-testid="rp-reference" />
          </div>

          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <Textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Tranche 2 of Kitchen package" data-testid="rp-notes" />
          </div>

          {!isVendor && (
            <label className="flex items-center gap-2 text-xs text-stone-600 cursor-pointer">
              <input type="checkbox" checked={isAdvance} onChange={e => setIsAdvance(e.target.checked)} className="w-3.5 h-3.5 accent-amber-600" data-testid="rp-is-advance" />
              Mark as <strong>advance</strong> (will display as <span className="font-mono text-amber-700">ADV-…</span> in the picker)
            </label>
          )}
        </div>

        <div className="px-6 py-3 border-t border-stone-200 bg-white flex items-center justify-end gap-2 flex-shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className={isVendor ? 'bg-blue-600 hover:bg-blue-700' : 'bg-emerald-600 hover:bg-emerald-700'} data-testid="rp-save-btn">
            {saving ? 'Recording…' : `Record ${isVendor ? 'Vendor' : 'Client'} Payment`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
